import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, AlertTriangle, Building, Calendar, Info, ExternalLink } from 'lucide-react';
import { parseDescription } from '@/lib/utils/descriptionParser';

interface ThreatSummaryProps {
  title?: string;
  name: string;
  mitreId: string;
  mitreUrl: string;
  coutry: string; // Changed from country
  year: string;   // Changed from firstseen
  targets: string[];
  aliases: string[];
  enrichment: string[];
  description: string;
  known_aliases?: string;
}

export function ThreatSummary({ 
  name,
  mitreId,
  mitreUrl,
  coutry, // Changed from country
  year,   // Changed from firstseen
  targets,
  aliases,
  enrichment,
  description,
  known_aliases = ''
}: ThreatSummaryProps) {
  const parsedDescription = parseDescription(description);
  // Safely handle known_aliases string, filtering out empty values
  const parsedAliases = known_aliases
    ? known_aliases.split(',')
        .map(alias => alias.trim())
        .filter(Boolean)
    : [];

  // Combine both sources of aliases and remove duplicates
  const allAliases = [...new Set([...parsedAliases, ...(aliases || [])])];

  {/* Removed console.log */}

  return (
    <Card>
      <CardHeader className="border-b">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-primary" />
            <div className="space-y-1">
              <h2 className="text-2xl font-semibold tracking-tight">{name}</h2>
              <div className="flex items-center gap-2">
                <a
                  href={mitreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                >
                  {mitreId}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <span className="text-sm text-muted-foreground">•</span>
                <span className="text-sm text-muted-foreground">Threat Actor Profile</span>
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Info className="h-4 w-4" />
            <span className="text-sm font-medium">Description</span>
          </div>
          <p className="text-foreground leading-relaxed">{parsedDescription}</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm font-medium">Country</span>
            </div>
            <p className="text-foreground">{coutry || 'Unknown'}</p> {/* Restored fallback */}
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span className="text-sm font-medium">Operating Since</span>
            </div>
            <p className="text-foreground">{year || 'Unknown'}</p> {/* Restored fallback */}
          </div>
        </div>

        {targets && targets.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Building className="h-4 w-4" />
              <span className="text-sm font-medium">Primary Targets</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {targets.map((target, index) => (
                <Badge key={index} variant="secondary">
                  {target}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {allAliases.length > 0 && (
          <div className="space-y-2">
            <span className="text-sm font-medium text-muted-foreground">Known Aliases</span>
            <div className="flex flex-wrap gap-2">
              {allAliases.map((alias, index) => (
                <Badge key={index} variant="outline">
                  {alias}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {enrichment && enrichment.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Info className="h-4 w-4" />
              <span className="text-sm font-medium">Additional Context</span>
            </div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {enrichment.map((point, index) => (
                <li key={index} className="flex gap-2">
                  <span className="select-none">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}